import React, { useState, useEffect, useCallback, useRef } from 'react';
import { debounce } from 'lodash';

// redux
import { useSelector } from 'react-redux';
import { selectFirebaseInfo } from '_redux/slices/main-screen/user';

// firebase
import { firestore } from "_utils/firebase-app";
import { collection, onSnapshot, query, where } from "firebase/firestore";

// bootstrap
import { Form } from 'react-bootstrap';

// styles
import { Spacing, Colors } from '_styles';

// icons
import { IoCloseCircle } from 'react-icons/io5';
import { FiUser } from 'react-icons/fi';

// components
import { CustomSpinner } from '_atoms';

/**
 * Reusable Employee Dropdown Component
 *
 * Listens to the "employee" Firestore collection in real-time
 * and displays a searchable dropdown of employees.
 *
 * @param {Object} props
 * @param {string} props.label - Label text for the dropdown
 * @param {string} props.placeholder - Placeholder text for the input
 * @param {function} props.onSelect - Callback when an employee is selected (receives employee object)
 * @param {function} props.onClear - Callback when selection is cleared
 * @param {boolean} props.required - Whether the field is required
 */
const EmployeeDropdown = ({
    label = 'Select Employee',
    placeholder = 'Search by name or ID...',
    onSelect,
    onClear,
    required = false,
}) => {
    const dbUser = useSelector(selectFirebaseInfo);

    // Local state
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredEmployees, setFilteredEmployees] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);

    // Refs
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    // Firebase listener for employees
    useEffect(() => {
        if (!dbUser?.details?.authority) return;

        setLoading(true);
        const employeeCollectionRef = collection(firestore, "employee");
        const employeeQuery = query(employeeCollectionRef, where("airport", "==", dbUser.details.authority));

        const unsubscribe = onSnapshot(employeeQuery, (snapshot) => {
            const updatedEmployeeList = [];
            snapshot.forEach((doc) => {
                const employeeData = doc.data();
                if (employeeData.airport === dbUser.details.authority) {
                    updatedEmployeeList.push(employeeData);
                }
            });
            setEmployees(updatedEmployeeList);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [dbUser?.details?.authority]);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Debounced filter
    const debouncedFilter = useCallback(
        debounce((term) => {
            if (!term) {
                setFilteredEmployees(employees);
                return;
            }
            const lower = term.toLowerCase();
            const filtered = employees.filter((emp) =>
                emp?.title?.toLowerCase().includes(lower) ||
                emp?.employeeId?.toLowerCase().includes(lower)
            );
            setFilteredEmployees(filtered);
        }, 300),
        [employees]
    );

    // Update filtered list when search term or employees change
    useEffect(() => {
        debouncedFilter(searchTerm);
        return () => debouncedFilter.cancel();
    }, [searchTerm, debouncedFilter]);

    // Handle search input change
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        setShowDropdown(true);

        if (selectedEmployee) {
            setSelectedEmployee(null);
            onClear?.();
        }
    };

    // Handle employee selection
    const handleEmployeeSelect = (employee) => {
        setSelectedEmployee(employee);
        setSearchTerm(employee?.title || employee?.employeeId || '');
        setShowDropdown(false);
        onSelect?.(employee);
    };

    // Handle clear selection
    const handleClear = () => {
        setSearchTerm('');
        setSelectedEmployee(null);
        setFilteredEmployees(employees);
        inputRef.current?.focus();
        onClear?.();
    };

    return (
        <Form.Group style={{ marginBottom: Spacing.SCALE_16 }}>
            <Form.Label style={{
                fontSize: 14,
                fontWeight: 600,
                color: '#374151',
                marginBottom: Spacing.SCALE_8
            }}>
                {label}
                {required && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
            </Form.Label>

            <div ref={dropdownRef} style={{ position: 'relative' }}>
                {/* Search Input */}
                <div style={{ position: 'relative' }}>
                    <div style={{
                        position: 'absolute',
                        left: 10,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        display: 'flex',
                        alignItems: 'center',
                    }}>
                        <FiUser size={18} color={Colors.PRM_LIGHT_BLUE} />
                    </div>

                    <Form.Control
                        ref={inputRef}
                        type="text"
                        placeholder={placeholder}
                        value={searchTerm}
                        onChange={handleSearchChange}
                        onFocus={() => setShowDropdown(true)}
                        style={{
                            fontSize: 14,
                            borderColor: selectedEmployee ? Colors.ARRIVALS_GREEN : '#e5e7eb',
                            borderRadius: 6,
                            paddingLeft: 36,
                            paddingRight: searchTerm ? 36 : 12,
                        }}
                    />

                    {searchTerm && (
                        <IoCloseCircle
                            size={20}
                            color="#999"
                            onClick={handleClear}
                            style={{
                                position: 'absolute',
                                right: 10,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                cursor: 'pointer',
                            }}
                        />
                    )}
                </div>

                {/* Dropdown Results */}
                {showDropdown && !selectedEmployee && (
                    <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: 6,
                        marginTop: 4,
                        maxHeight: 250,
                        overflowY: 'auto',
                        zIndex: 1000,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    }}>
                        {loading && (
                            <div style={{ padding: Spacing.SCALE_16, textAlign: 'center' }}>
                                <CustomSpinner size="small" />
                                <p style={{ margin: '8px 0 0', fontSize: 12, color: '#6b7280' }}>
                                    Loading employees...
                                </p>
                            </div>
                        )}

                        {!loading && filteredEmployees.length === 0 && (
                            <div style={{ padding: Spacing.SCALE_16, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>
                                No employees found{searchTerm ? ` for "${searchTerm}"` : ''}
                            </div>
                        )}

                        {!loading && filteredEmployees.length > 0 && (
                            filteredEmployees.map((employee) => (
                                <div
                                    key={employee.employeeId}
                                    onClick={() => handleEmployeeSelect(employee)}
                                    style={{
                                        padding: Spacing.SCALE_12,
                                        borderBottom: '1px solid #f3f4f6',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: Spacing.SCALE_12,
                                        transition: 'background-color 0.15s',
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                >
                                    <div style={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: '50%',
                                        backgroundColor: employee?.status === 'online' ? 'green' : 'red',
                                        flexShrink: 0,
                                    }} />
                                    <FiUser size={20} color={Colors.PRM_LIGHT_BLUE} />
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 600, fontSize: 14, color: '#374151' }}>
                                            {employee?.title || 'Unknown'}
                                        </div>
                                        <div style={{ fontSize: 12, color: '#6b7280' }}>
                                            {employee?.employeeId}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>

            {/* Selected Employee Info */}
            {selectedEmployee && (
                <div style={{
                    marginTop: Spacing.SCALE_12,
                    padding: Spacing.SCALE_12,
                    backgroundColor: '#f0fdf4',
                    borderRadius: 6,
                    border: `1px solid ${Colors.ARRIVALS_GREEN}`,
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: Spacing.SCALE_8 }}>
                        <FiUser size={24} color={Colors.ARRIVALS_GREEN} />
                        <div>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>
                                {selectedEmployee?.title || 'Unknown'}
                            </div>
                            <div style={{ fontSize: 12, color: '#6b7280' }}>
                                {selectedEmployee?.employeeId}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Form.Group>
    );
};

export default EmployeeDropdown;
